import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { fetchEmailById } from "../../../utils/api";
import {
  formatDateTime,
  formatEmailAddress,
  getErrorMessage,
} from "./emailFormatters";

const participantName = (email, listItem) =>
  listItem?.patientName || email.patientName || email.patientId || "Unmatched participant";

const followUpLabel = (email, listItem) =>
  listItem?.displayFollowUpCode || email.displayFollowUpCode || "";

const routeLabelSx = {
  color: "#500000",
  fontWeight: 700,
  mr: 1,
};

const emailHtmlSrcDoc = (bodyHtml) => `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data: https:; style-src 'unsafe-inline';" />
    <style>
      body {
        color: #1f2933;
        font-family: Arial, sans-serif;
        font-size: 16px;
        line-height: 1.6;
        margin: 0;
        overflow-wrap: anywhere;
      }
      p {
        margin: 0 0 16px;
      }
    </style>
  </head>
  <body>${bodyHtml || ""}</body>
</html>`;

const EmailDetailDrawer = ({ emailId, listItem, open, onClose, onMatchPatient }) => {
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const normalizeDetail = (response) => {
    const payload = response?.data || response || {};
    if (payload.message) {
      return payload.message;
    }
    return payload;
  };

  useEffect(() => {
    if (!open || !emailId) return;

    const loadDetail = async () => {
      setLoading(true);
      setEmail(null);
      setError("");
      try {
        const response = await fetchEmailById(emailId);
        const normalized = normalizeDetail(response);
        setEmail(normalized);
      } catch (err) {
        console.error("Failed to load email detail:", err);
        setError(getErrorMessage(err, "Failed to load email detail."));
      } finally {
        setLoading(false);
      }
    };

    loadDetail();
  }, [emailId, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{email ? `Subject: ${email.subject || "N/A"}` : "Email Detail"}</DialogTitle>
      <DialogContent dividers sx={{ p: 3 }}>
        {loading && (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && email && (
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 0.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#500000", textAlign: "center" }}>
                {participantName(email, listItem)}
              </Typography>
              <Tooltip title="Edit Participant Match" arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => onMatchPatient?.(email)}
                    disabled={!email}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>

            <Stack
              spacing={0.5}
              sx={{
                color: "text.secondary",
                overflowWrap: "anywhere",
                lineHeight: 1.5,
                wordBreak: "normal",
              }}
            >
              <Typography variant="body1" sx={{ overflowWrap: "anywhere", lineHeight: 1.5 }}>
                <Box component="span" sx={routeLabelSx}>From</Box>
                <Box component="span">{formatEmailAddress(email.fromAddress)}</Box>
              </Typography>
              <Typography variant="body1" sx={{ overflowWrap: "anywhere", lineHeight: 1.5 }}>
                <Box component="span" sx={routeLabelSx}>To</Box>
                <Box component="span">{formatEmailAddress(email.toAddress)}</Box>
              </Typography>
            </Stack>

            <Box>
              {email.bodyHtml ? (
                <Box
                  component="iframe"
                  title="Email HTML body"
                  sandbox=""
                  srcDoc={emailHtmlSrcDoc(email.bodyHtml)}
                  sx={{
                    display: "block",
                    width: "100%",
                    height: 300,
                    p: 2,
                    bgcolor: "#fafafa",
                    border: "1px solid #e0e0e0",
                  }}
                />
              ) : email.bodyText ? (
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#fafafa",
                    border: "1px solid #e0e0e0",
                    maxHeight: 260,
                    overflow: "auto",
                    whiteSpace: "pre-wrap",
                    fontFamily: "inherit",
                    fontSize: "1rem",
                    lineHeight: 1.6,
                  }}
                >
                  {email.bodyText}
                </Box>
              ) : (
                <Alert severity="info">No body content is available for this email.</Alert>
              )}
            </Box>

            <Typography variant="caption" sx={{ color: "text.secondary", overflowWrap: "anywhere", lineHeight: 1.6 }}>
              {[
                followUpLabel(email, listItem),
                email.time ? formatDateTime(email.time) : "",
                email.provider,
                email.threadKey ? `Thread ${email.threadKey}` : "",
              ].filter(Boolean).join("  |  ")}
            </Typography>

          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", py: 2 }}>
        <Button variant="contained" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailDetailDrawer;
